import { MainButton } from '@lovoldsystem/ui'
import { Close, InfoRounded, WarningAmberRounded } from '@mui/icons-material'
import { Box, IconButton, Typography, styled, useTheme, SxProps, Theme } from '@mui/material'
import { FC } from 'react'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { CancelButton } from './CancelButton'

interface ButtonConfig {
  text: string
  variant?: 'contained' | 'outlined' | 'text'
  sx?: SxProps<Theme>
  callback: (pop) => void
}

interface WarningDialogProps extends ShowDialogProps {
  title: string
  description?: string
  isCancelButtonShow?: boolean
  buttons?: ButtonConfig[]
  variant?: 'warning' | 'info'
}

const StyledFlexColumn = styled(Flex.Column)({
  width: '500px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '32px',
  position: 'relative',
})

const StyledCloseButton = styled(IconButton)({
  position: 'absolute',
  top: '16px',
  right: '16px',
})

const StyledBox = styled(Box)({
  width: '80px',
  height: '80px',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
})

export const WarningDialog: FC<WarningDialogProps> = ({
  title,
  description,
  pop,
  isCancelButtonShow = true,
  buttons = [],
  variant = 'warning',
}) => {
  const theme = useTheme()
  const isInfo = variant === 'info'

  const defaultButtonSx: SxProps<Theme> = isInfo
    ? {
      width: '156px',
      height: '44px',
      bgcolor: theme.palette.primaryDark[theme.palette.mode],
      ':hover': { bgcolor: theme.palette.primaryDark[theme.palette.mode] },
    }
    : {
      width: '156px',
      height: '44px',
      bgcolor: theme.palette.errorMain[theme.palette.mode],
      ':hover': { bgcolor: theme.palette.errorLight[theme.palette.mode] },
    }

  const cancelButtonSx: SxProps<Theme> = {
    width: '156px',
    height: '44px',
    color: theme.palette.black[theme.palette.mode],
    borderColor: theme.palette.black[theme.palette.mode],
    ':hover': { borderColor: theme.palette.black[theme.palette.mode] },
  }

  return (
    <StyledFlexColumn spacing={'16px'}>
      <StyledCloseButton aria-label="close" onClick={pop}>
        <Close sx={{ color: theme.palette.black[theme.palette.mode] }} />
      </StyledCloseButton>
      <StyledBox bgcolor={isInfo ? theme.palette.primaryLight[theme.palette.mode] + '1A' : theme.palette.errorLight[theme.palette.mode] + '1A'}>
        {isInfo
          ? <InfoRounded htmlColor={theme.palette.primaryDark[theme.palette.mode]} sx={{ height: '44px', width: '48px' }} />
          : <WarningAmberRounded htmlColor={theme.palette.errorDark[theme.palette.mode]} sx={{ height: '44px', width: '48px' }} />
        }
      </StyledBox>
      <Typography textAlign={'center'} fontSize={'24px'} color={theme.palette.primaryMain[theme.palette.mode]}>
        {title}
      </Typography>
      <Box
        sx={{
          marginTop: '8px !important',
        }}
      >
        <Typography
          variant="subtitle1"
          color={theme.palette.black[theme.palette.mode]}
          textAlign={'center'}
          fontSize={'18px'}
          lineHeight={'1.4'}
        >
          {description}
        </Typography>
      </Box>
      <Flex.Row gap={2}>
        {isCancelButtonShow && <CancelButton onClick={pop} />}
        {buttons.map((button, index) => (
          <MainButton
            key={index}
            variant={button.variant}
            sx={button.sx ? { ...defaultButtonSx, ...button.sx } : defaultButtonSx}
            onClick={() => {
              pop()
              button.callback(pop)
            }}
          >
            <Typography  variant="subtitle1">
              {button.text}
            </Typography>
          </MainButton>
        ))}
      </Flex.Row>
    </StyledFlexColumn>
  )
}
